import { useCallback, useEffect, useRef, useState } from "react";
import "./AutoSlider.scss";
import { UseInterval } from "./UseInterval";
import { useNavigate } from "react-router-dom";

const DEFAULT_BADGES = [
    { title: "10% OFF", subtitle: "Shop Now" },
    { title: "New", subtitle: "Shop Now" },
    { title: "Bestseller", subtitle: "Shop Now" },
    { title: "20% OFF", subtitle: "Shop Now" },
    { title: "Hot deal", subtitle: "Shop Now" },
    { title: "Limited", subtitle: "Shop Now" },
];

function OfferBadge({ badge }) {
    if (!badge) return null;
    return (
        <div className="auto-slider__offer">
            <span className="auto-slider__offer-title">{badge.title}</span>
            <span className="auto-slider__offer-sub">{badge.subtitle}</span>
        </div>
    );
}

function SlideCard({ imgObj, width, onNavigate, showBadge, badge }) {
    return (
        <div
            className="auto-slider__card"
            style={{ width }}
            onClick={() => onNavigate(imgObj.url)}
            role="link"
            tabIndex={0}
            onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onNavigate(imgObj.url);
                }
            }}
        >
            <img src={imgObj.img} alt="" />
            {showBadge ? <OfferBadge badge={badge} /> : null}
        </div>
    );
}

/**
 * Infinite strip: [pre clones][main][post clones]
 * Start at -imagesToShow (first main card). When we reach the post-clone
 * start (visually identical to main start), snap back with transition:none
 * then continue — avoids the blank frame from overshooting past clones.
 */
export function AutoSlider({ imagesToShow = 6, images = [] }) {
    const count = images.length;
    const mainStart = -imagesToShow;
    /** Post-clone strip starts here; looks the same as mainStart. */
    const loopPoint = -(count + imagesToShow);

    const [slideXPos, setSlideXPos] = useState(mainStart);
    const movePercent = 100 / imagesToShow;
    const [swichSlides, setSwichSlides] = useState({
        transform: `translateX(${mainStart * movePercent}%)`,
        transition: "none",
    });
    const [delay, setDelay] = useState(null);
    const navigate = useNavigate();
    const snappingRef = useRef(false);

    const getBadge = (index) => {
        const custom = images[index]?.badge;
        if (custom) return custom;
        return DEFAULT_BADGES[index % DEFAULT_BADGES.length];
    };

    useEffect(() => {
        if (!count) return;
        setDelay(1800);
    }, [count]);

    const advance = useCallback(() => {
        if (!count || snappingRef.current) return;

        // At start of post clones (== visual start of main): snap, then step once.
        if (slideXPos <= loopPoint) {
            snappingRef.current = true;
            setSwichSlides({
                transform: `translateX(${mainStart * movePercent}%)`,
                transition: "none",
            });
            setSlideXPos(mainStart);

            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    const next = mainStart - 1;
                    setSwichSlides({
                        transform: `translateX(${next * movePercent}%)`,
                        transition: "transform 0.85s linear",
                    });
                    setSlideXPos(next);
                    snappingRef.current = false;
                });
            });
            return;
        }

        const next = slideXPos - 1;
        setSwichSlides({
            transform: `translateX(${next * movePercent}%)`,
            transition: "transform 0.85s linear",
        });
        setSlideXPos(next);
    }, [count, loopPoint, mainStart, movePercent, slideXPos]);

    UseInterval(() => {
        advance();
    }, delay);

    const pauseAuto = () => setDelay(null);
    const resumeAuto = () => setDelay(2200);
    const width = `${movePercent}%`;

    if (!count) return null;

    const preClones = images.slice(Math.max(0, count - imagesToShow));
    const postClones = images.slice(0, imagesToShow);

    return (
        <div
            className="auto-slider"
            onMouseEnter={pauseAuto}
            onMouseLeave={resumeAuto}
        >
            <div className="specific-slide-main">
                <div className="specific-slide-container">
                    <div className="container">
                        <div className="main">
                            <div className="slides" style={swichSlides}>
                                {preClones.map((imgObj, key) => {
                                    const realIndex = count - preClones.length + key;
                                    return (
                                        <SlideCard
                                            key={`pre-${key}`}
                                            imgObj={imgObj}
                                            width={width}
                                            onNavigate={navigate}
                                            showBadge
                                            badge={getBadge(realIndex)}
                                        />
                                    );
                                })}
                                {images.map((imgObj, key) => (
                                    <SlideCard
                                        key={`main-${key}`}
                                        imgObj={imgObj}
                                        width={width}
                                        onNavigate={navigate}
                                        showBadge
                                        badge={getBadge(key)}
                                    />
                                ))}
                                {postClones.map((imgObj, key) => (
                                    <SlideCard
                                        key={`post-${key}`}
                                        imgObj={imgObj}
                                        width={width}
                                        onNavigate={navigate}
                                        showBadge
                                        badge={getBadge(key)}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
